/**
 * Shared building blocks for MainSettings admin panels:
 *   - LANGS: locale metadata
 *   - BADGE_ICONS: icon registry (must match Home.jsx)
 *   - Section: collapsible container card
 *   - ImageUploader: file+URL image field with base64 encoding
 *   - MultiLangText: 3-column PT/EN/ES text input group
 */
import React, { useRef, useState } from "react";
import {
  ChevronDown, ChevronRight, Upload, X,
  Trophy, Star, Crown, Medal, Award, Sparkles, Zap, Flame,
  Heart, ThumbsUp, Rocket, Target, Gem, Diamond,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const LANGS = [
  { code: "pt", flag: "🇵🇹", label: "PT" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "es", flag: "🇪🇸", label: "ES" },
];

/** Icon registry for the champion-badge picker — must match Home.jsx ICONS. */
export const BADGE_ICONS = {
  Trophy, Star, Crown, Medal, Award, Sparkles, Zap, Flame,
  Heart, ThumbsUp, Rocket, Target, Gem, Diamond,
};

export const Section = ({ id, icon: Icon, title, children, open, onToggle }) => (
  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={`section-${id}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition rounded-t-2xl"
      data-testid={`section-toggle-${id}`}
    >
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-sky-600" />
        <h3 className="font-[Fredoka] font-bold text-slate-900">{title}</h3>
      </div>
      {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
    </button>
    {open && <div className="p-5 border-t border-slate-100 space-y-4">{children}</div>}
  </section>
);

/**
 * ImageUploader — combines a file picker + URL input + preview.
 * Files are converted to a base64 data URL client-side and written
 * straight to the config field via `onChange(dataUrl)`. Max 2 MB.
 */
export const ImageUploader = ({ label, value, onChange, testid, previewClass = "h-12 mt-2 object-contain" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas ficheiros de imagem são permitidos."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 2 MB)."); return; }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => { onChange(reader.result); setBusy(false); toast.success(`${label} carregado (${(file.size / 1024).toFixed(0)} KB)`); };
    reader.onerror = () => { setBusy(false); toast.error("Erro ao ler ficheiro."); };
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <Label className="text-sm font-bold text-slate-700">{label}</Label>
      <div className="flex flex-col sm:flex-row gap-2 mt-1">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testid}-file`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="rounded-lg flex-shrink-0"
          data-testid={`${testid}-upload-btn`}
        >
          <Upload className="w-4 h-4 mr-1.5" /> {busy ? "A carregar…" : "Carregar imagem"}
        </Button>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… ou cola URL aqui"
          data-testid={testid}
        />
        {value && (
          <Button
            type="button"
            variant="outline"
            onClick={() => onChange("")}
            className="rounded-lg flex-shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50"
            data-testid={`${testid}-remove`}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      {value && (
        <div className="mt-2">
          <img src={value} alt={`${label} preview`} className={previewClass} />
        </div>
      )}
    </div>
  );
};

export const MultiLangText = ({ label, value, onChange, multiline = false, placeholder, testidPrefix }) => (
  <div>
    <Label className="text-sm font-bold text-slate-700">{label}</Label>
    <div className="grid sm:grid-cols-3 gap-2 mt-1">
      {LANGS.map(({ code, flag, label: lab }) => (
        <div key={code}>
          <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><span>{flag}</span> {lab}</div>
          {multiline ? (
            <textarea
              value={value?.[code] || ""}
              onChange={(e) => onChange(code, e.target.value)}
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-[Nunito] focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder={placeholder}
              data-testid={`${testidPrefix}-${code}`}
            />
          ) : (
            <Input
              value={value?.[code] || ""}
              onChange={(e) => onChange(code, e.target.value)}
              placeholder={placeholder}
              data-testid={`${testidPrefix}-${code}`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);
